// LeaderboardComponent.tsx
import React, { useEffect, useState } from "react";
import styled, { keyframes } from "styled-components";
import { getTopUsers } from "../api/user";
import { useSelector } from "react-redux";
import { Leaderboard, RootState } from "../redux/type";
import { useDispatch } from "react-redux";
import { setLeaderboard } from "../redux/actions";
import { motion } from "framer-motion";
import toast from "react-hot-toast";
import { formatNumber } from "../utils/helpers";
import LeaderboardEntryComponent from "../components/Leaderboard/LeaderboardEntry";
import refreshIcon from "../assets/icons/refresh.png";
import { Search } from "lucide-react";

// Animations
const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

const shine = keyframes`
  0% { background-position: -100px; }
  40%, 100% { background-position: 140px; }
`;

// Styled Components
const LeaderboardWrapper = styled.div`
  margin-bottom: 100px;
  margin-top: 1rem;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
`;

const SearchIcon = styled(Search)`
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  width: 1rem;
  height: 1rem;
  opacity: 0.7;
  color: white;
`;

const LeaderboardContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-top: 1.5rem;
  width: 90%;
  margin-left: auto;
  margin-right: auto;
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: white;
  text-align: center;
  margin: 0 0 1.5rem;
  background: linear-gradient(90deg, #ccff00, #3b82f6);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  position: relative;
  padding-bottom: 0.5rem;

  &:after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 3px;
    background: linear-gradient(90deg, #ccff00, #3b82f6);
    border-radius: 3px;
  }
`;

const Subtitle = styled.p`
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 1.5rem;
`;

const TopThreeContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-end;
  gap: 1rem;
  margin: 2rem 0;
  position: relative;
`;

const TopPlayer = styled.div<{ position: number }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  gap: 0.1rem;
  width: ${(props) => (props.position === 2 ? "120px" : "100px")};

  ${(props) =>
    props.position === 1 &&
    `
    order: 2;
    margin-bottom: 20px;
  `}

  ${(props) =>
    props.position === 2 &&
    `
    order: 1;
  `}
  
  ${(props) =>
    props.position === 3 &&
    `
    order: 3;
  `}
`;

const TopAvatar = styled.div<{ position: number }>`
  width: ${(props) => (props.position === 1 ? "70px" : "60px")};
  height: ${(props) => (props.position === 1 ? "70px" : "60px")};
  border-radius: 50%;
  background: ${(props) =>
    props.position === 1
      ? "linear-gradient(135deg, #ffd700, #f5a623)"
      : props.position === 2
      ? "linear-gradient(135deg, #c0c0c0, #a0a0a0)"
      : "linear-gradient(135deg, #cd7f32, #a0522d)"};
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: ${(props) => (props.position === 1 ? "1.5rem" : "1.2rem")};
  font-weight: 700;
  color: #000;
  margin-bottom: 0.5rem;
  border: 3px solid
    ${(props) =>
      props.position === 1
        ? "#ffd700"
        : props.position === 2
        ? "#c0c0c0"
        : "#cd7f32"};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
`;

const TopUsername = styled.div<{ position: number }>`
  font-size: ${(props) => (props.position === 1 ? "0.9rem" : "0.7rem")};
  font-weight: 600;
  color: white;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
`;

const TopRank = styled.div<{ position: number }>`
  font-size: ${(props) => (props.position === 1 ? "1.0rem" : "1.0rem")};
  font-weight: 600;
  color: white;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
`;

const TopPoints = styled.div<{ position: number }>`
  font-size: ${(props) => (props.position === 1 ? "1rem" : "0.8rem")};
  color: ${(props) =>
    props.position === 1
      ? "#ffd700"
      : props.position === 2
      ? "#c0c0c0"
      : "#cd7f32"};
  font-weight: 600;
`;

const TopPosition = styled.div<{ position: number }>`
  position: absolute;
  top: ${(props) => (props.position === 1 ? "-20px" : "-15px")};
  background: ${(props) =>
    props.position === 1
      ? "rgba(255, 215, 0, 0.2)"
      : props.position === 2
      ? "rgba(192, 192, 192, 0.2)"
      : "rgba(205, 127, 50, 0.2)"};
  color: ${(props) =>
    props.position === 1
      ? "#ffd700"
      : props.position === 2
      ? "#c0c0c0"
      : "#cd7f32"};
  padding: 0.2rem 0.5rem;
  border-radius: 20px;
  font-size: ${(props) => (props.position === 1 ? "0.9rem" : "0.8rem")};
  font-weight: 700;
  border: 1px solid
    ${(props) =>
      props.position === 1
        ? "#ffd700"
        : props.position === 2
        ? "#c0c0c0"
        : "#cd7f32"};
`;

const TabsContainer = styled.div`
  display: flex;
  width: 100%;
  border-bottom: 2px solid rgba(255, 255, 255, 0.2);
  margin: 2rem 0;
`;
const Tab = styled.button<{ active: boolean }>`
  flex: 1;
  background: none;
  border: none;
  color: ${(props) => (props.active ? "#ccff00" : "rgba(255, 255, 255, 0.7)")};
  font-weight: 600;
  font-size: 1rem;
  padding: 1rem 0;
  text-align: center;
  position: relative;
  cursor: pointer;
  transition: color 0.3s ease;

  &:hover {
    color: #ccff00;
  }

  &::after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 0;
    height: 3px;
    width: 100%;
    background: #ccff00;
    transform: scaleX(${(props) => (props.active ? 1 : 0)});
    transform-origin: center;
    transition: transform 0.3s ease-in-out;
  }
`;

const SearchContainer = styled.div`
  position: relative;
  margin: 1rem 0;
  width: 90%;
  margin: 0 auto;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 2.5rem;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  font-size: 0.9rem;

  &:focus {
    outline: none;
    border-color: #ccff00;
    background: rgba(0, 224, 255, 0.1);
  }
`;

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 90%;
  margin: 0 auto;
`;

const StatsBadge = styled.div`
  background: rgba(0, 224, 255, 0.1);
  color: #ccff00;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 600;
  border: 1px solid rgba(0, 224, 255, 0.3);
`;

const RefreshButton = styled(motion.button)`
  background: transparent;
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: rotate(180deg);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.2),
      transparent
    );
    transition: none;
  }

  &:hover::after {
    animation: ${shine} 1.5s infinite;
  }

  img {
    width: 2rem;
    height: 2rem;
  }
`;

const LeaderboardComponent = () => {
  const leaderboard = useSelector((state: RootState) => state.leaderboard);
  const user = useSelector((state: RootState) => state.userData);
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("alltime");
  const [searchQuery, setSearchQuery] = useState("");
  const userId = localStorage.getItem("userId");

  const updateLeaderboard = async () => {
    setLoading(true);
    const toastId = toast.loading("Refreshing leaderboard...");
    try {
      const newLeaderboard: Leaderboard = await getTopUsers(userId);
      dispatch(setLeaderboard(newLeaderboard));
      toast.success("Leaderboard updated!", {
        id: toastId,
        icon: "🏆",
      });
    } catch (error) {
      toast.error("Failed to update leaderboard", { id: toastId });
    } finally {
      setLoading(false);
    }
  };

  const getPointsForTab = (entry: any) => {
    switch (activeTab) {
      case "daily":
        return entry.dailyPoints || 0;
      case "weekly":
        return entry.weeklyPoints || 0;
      default:
        return entry.points || 0;
    }
  };

  // const sortedEntries = [...leaderboard].sort(
  //   (a, b) => getPointsForTab(b) - getPointsForTab(a)
  // );

  // const filteredEntries = sortedEntries.filter((entry) =>
  //   entry.username.toLowerCase().includes(searchQuery.toLowerCase())
  // );

  return (
    <LeaderboardWrapper>
      <SectionTitle> Leaderboard</SectionTitle>
      {/* Leaderboard */}
      {/* <HeaderContainer>
        <SectionTitle>
          <span>Players</span>
        </SectionTitle>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <StatsBadge>{formatNumber(filteredEntries.length)} Players</StatsBadge>
          <RefreshButton onClick={updateLeaderboard} disabled={loading} whileTap={{ scale: 0.9 }}>
            <img src={refreshIcon} alt="Refresh" />
          </RefreshButton>
        </div>
      </HeaderContainer> */}
      {/* Top 3 Players */}
      <TopThreeContainer>
        {leaderboard[activeTab]?.slice(0, 3).map((player, index) => (
          <TopPlayer key={player.username} position={index + 1}>
            <TopAvatar position={index + 1}>
              {player?.profilePicture ? (
                <img
                  src={player.profilePicture}
                  alt="User Avatar"
                  style={{
                    width: "100%",
                    height: "100%",
                    borderRadius: "50%",
                    objectFit: "cover",
                  }}
                />
              ) : (
                player.username.slice(0, 2).toUpperCase()
              )}
            </TopAvatar>
            <TopRank position={index + 1}>#{index + 1}</TopRank>
            <TopUsername position={index + 1}>{player.username}</TopUsername>
            <TopPoints position={index + 1}>
              {formatNumber(player.points)} pts
            </TopPoints>
          </TopPlayer>
        ))}
      </TopThreeContainer>

      {/* Tabs */}
      <TabsContainer>
        <Tab
          active={activeTab === "daily"}
          onClick={() => setActiveTab("daily")}
        >
          Daily
        </Tab>
        <Tab
          active={activeTab === "weekly"}
          onClick={() => setActiveTab("weekly")}
        >
          Weekly
        </Tab>
        <Tab
          active={activeTab === "alltime"}
          onClick={() => setActiveTab("alltime")}
        >
          All Time
        </Tab>
      </TabsContainer>

      {/* Search */}
      <SearchContainer>
        <SearchIcon as={Search} />
        <SearchInput
          type="text"
          placeholder="Search players..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </SearchContainer>

      <LeaderboardContainer>
        {leaderboard[activeTab]?.slice(3).map((user, index) => (
          <motion.div
            key={user.username}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05 }}
          >
            <LeaderboardEntryComponent index={index + 3} user={user} />
          </motion.div>
        ))}
      </LeaderboardContainer>
    </LeaderboardWrapper>
  );
};

export default LeaderboardComponent;
